// @ts-check
import { UNCATEGORIZED_CATEGORY, sanitizeImageTitle } from '../../shared/catalog.js';

/** How many URLs or files one bulk run will send. */
export const BULK_IMPORT_LIMIT = 40;

/**
 * Pulls unique http(s) URLs out of a newline-separated list.
 * Blank lines and lines starting with # are skipped.
 *
 * @param {string} text
 * @returns {string[]}
 */
export function parseBulkUrlList(text) {
  /** @type {string[]} */
  const urls = [];
  const seen = new Set();
  for (const line of String(text || '').split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const match = trimmed.match(/https?:\/\/[^\s<>"']+/i);
    const candidate = match ? match[0] : trimmed;
    try {
      const url = new URL(candidate);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') continue;
      const href = url.toString();
      if (seen.has(href)) continue;
      seen.add(href);
      urls.push(href);
    } catch {
      // skip lines that are not URLs
    }
    if (urls.length >= BULK_IMPORT_LIMIT) break;
  }
  return urls;
}

/**
 * A short catalog title from a file name or URL path.
 *
 * @param {string} source
 * @returns {string}
 */
export function titleFromImageSource(source) {
  const leaf = String(source || '').split('?')[0].split('/').pop() || '';
  const withoutExt = leaf.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ');
  return sanitizeImageTitle(withoutExt) || 'Imported image';
}

/**
 * Body for one admin URL import. Bulk imports are always SFW and unpublished.
 *
 * @param {string} url
 * @returns {{ url: string, title: string, category: string, tags: string[], description: string, is_active: number, is_nsfw: number }}
 */
export function bulkUrlImportBody(url) {
  return {
    url,
    title: titleFromImageSource(url),
    category: UNCATEGORIZED_CATEGORY,
    tags: [],
    description: '',
    is_active: 0,
    is_nsfw: 0
  };
}

/**
 * @param {File} file
 * @returns {Promise<{ width: number, height: number }>}
 */
function readImageSize(file) {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    const finish = (width, height) => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: width > 0 ? width : 1920,
        height: height > 0 ? height : 1080
      });
    };
    image.onload = () => finish(image.naturalWidth, image.naturalHeight);
    image.onerror = () => finish(1920, 1080);
    image.src = objectUrl;
  });
}

/**
 * @param {HTMLElement} list
 * @param {string} label
 * @param {'pending'|'ok'|'error'} state
 * @param {string} [detail]
 * @returns {HTMLLIElement}
 */
function appendResult(list, label, state, detail) {
  const item = document.createElement('li');
  item.className = state === 'ok' ? 'is-ok' : (state === 'error' ? 'is-error' : '');
  item.textContent = detail ? `${label} — ${detail}` : label;
  list.appendChild(item);
  return item;
}

/**
 * Wires the bulk upload dialog. Imported images are SFW and unpublished.
 *
 * @param {{
 *   apiBase: string,
 *   getToken: () => string,
 *   onImported?: () => void
 * }} options
 */
export function initBulkUpload(options) {
  const modal = document.getElementById('bulk-upload-modal');
  const openBtn = document.getElementById('open-bulk-upload-btn');
  const closeBtn = document.getElementById('close-bulk-upload-modal');
  const cancelBtn = document.getElementById('cancel-bulk-upload-btn');
  const submitBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById('submit-bulk-upload-btn'));
  const urlInput = /** @type {HTMLTextAreaElement|null} */ (document.getElementById('bulk-url-list'));
  const fileInput = /** @type {HTMLInputElement|null} */ (document.getElementById('bulk-file-input'));
  const dropzone = document.getElementById('bulk-dropzone');
  const fileCount = document.getElementById('bulk-file-count');
  const results = document.getElementById('bulk-upload-results');
  if (!modal || !openBtn || !submitBtn || !urlInput || !fileInput || !results) return;

  /** @type {File[]} */
  let files = [];

  const paintFileCount = () => {
    if (!fileCount) return;
    fileCount.textContent = files.length ? `${files.length} file${files.length === 1 ? '' : 's'} selected` : '';
  };

  const close = () => {
    modal.classList.remove('open');
  };

  const open = () => {
    urlInput.value = '';
    files = [];
    fileInput.value = '';
    results.replaceChildren();
    paintFileCount();
    submitBtn.disabled = false;
    modal.classList.add('open');
    urlInput.focus();
  };

  /**
   * @param {File[]} incoming
   */
  const addFiles = (incoming) => {
    for (const file of incoming) {
      if (!file || !String(file.type || '').startsWith('image/')) continue;
      if (files.length >= BULK_IMPORT_LIMIT) break;
      files.push(file);
    }
    paintFileCount();
  };

  openBtn.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  cancelBtn?.addEventListener('click', close);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.classList.contains('open')) close();
  });

  dropzone?.addEventListener('click', () => fileInput.click());
  dropzone?.addEventListener('keydown', (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      fileInput.click();
    }
  });
  dropzone?.addEventListener('dragover', (event) => {
    event.preventDefault();
  });
  dropzone?.addEventListener('drop', (event) => {
    event.preventDefault();
    addFiles(Array.from(event.dataTransfer?.files || []));
  });
  fileInput.addEventListener('change', () => {
    addFiles(Array.from(fileInput.files || []));
    fileInput.value = '';
  });

  submitBtn.addEventListener('click', () => {
    void runImport();
  });

  /**
   * @returns {Promise<void>}
   */
  async function runImport() {
    const urls = parseBulkUrlList(urlInput.value);
    const batch = files.slice(0, BULK_IMPORT_LIMIT);
    if (!urls.length && !batch.length) {
      results.replaceChildren();
      appendResult(results, 'Add at least one URL or image file.', 'error');
      return;
    }
    submitBtn.disabled = true;
    results.replaceChildren();
    const token = options.getToken();
    const auth = token ? { Authorization: 'Bearer ' + token } : {};
    let imported = 0;

    for (const url of urls) {
      const row = appendResult(results, url, 'pending', 'Importing…');
      try {
        const res = await fetch(options.apiBase + '/api/admin/images/url-import', {
          method: 'POST',
          headers: { ...auth, 'Content-Type': 'application/json' },
          body: JSON.stringify(bulkUrlImportBody(url))
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          row.className = 'is-error';
          row.textContent = `${url} — ${data.error || 'Import failed'}`;
          continue;
        }
        imported += 1;
        row.className = 'is-ok';
        row.textContent = `${url} — saved unpublished`;
      } catch (err) {
        row.className = 'is-error';
        row.textContent = `${url} — ${err instanceof Error ? err.message : 'Import failed'}`;
      }
    }

    for (const file of batch) {
      const label = file.name || 'image';
      const row = appendResult(results, label, 'pending', 'Uploading…');
      try {
        const size = await readImageSize(file);
        const form = new FormData();
        form.append('file', file);
        form.append('title', titleFromImageSource(file.name));
        form.append('category', UNCATEGORIZED_CATEGORY);
        form.append('tags', '[]');
        form.append('description', '');
        form.append('width', String(size.width));
        form.append('height', String(size.height));
        form.append('is_active', '0');
        form.append('is_nsfw', '0');
        const res = await fetch(options.apiBase + '/api/admin/images', {
          method: 'POST',
          headers: auth,
          body: form
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          row.className = 'is-error';
          row.textContent = `${label} — ${data.error || 'Upload failed'}`;
          continue;
        }
        imported += 1;
        row.className = 'is-ok';
        row.textContent = `${label} — saved unpublished`;
      } catch (err) {
        row.className = 'is-error';
        row.textContent = `${label} — ${err instanceof Error ? err.message : 'Upload failed'}`;
      }
    }

    files = [];
    paintFileCount();
    submitBtn.disabled = false;
    if (imported > 0) options.onImported?.();
  }
}
